import * as React from 'react'
import {
  CalendarDays,
  Clock,
  HeartHandshake,
  Pencil,
  Plus,
  Target,
  Trash2,
  UserRound,
} from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { formatDate, localDate } from '@/lib/format'
import type {
  CoachingSession,
  CoachingSessionGoal,
  CommonGoal,
  TeamGoal,
  TeamMember,
} from '@/lib/types'
import { goalIcon } from '@/lib/goal-icons'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { EmptyState, ErrorNote, Loading, Markdown, Page } from '@/components/page'
import { DiscardPrompt } from '@/components/discard-prompt'

/** The roster label, falling back the same way everywhere else in the portal does. */
function memberName(m: TeamMember): string {
  return m.display_name?.trim() || m.email.split('@')[0]
}

/** "3 weeks ago" in whole days, for the roster's at-a-glance column. */
function sinceLabel(iso: string): string {
  const days = Math.round((Date.now() - localDate(iso).getTime()) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 14) return `${days} days ago`
  if (days < 60) return `${Math.round(days / 7)} weeks ago`
  return `${Math.round(days / 30)} months ago`
}

export function CoachingPage() {
  const { currentTeam } = useAuth()
  const teamId = currentTeam?.id
  const [selected, setSelected] = React.useState<string | null>(null)
  const [editing, setEditing] = React.useState<CoachingSession | 'new' | null>(null)

  const { data, loading, error, refetch } = useQuery(async () => {
    if (!teamId) {
      return {
        members: [] as TeamMember[],
        sessions: [] as CoachingSession[],
        tags: [] as CoachingSessionGoal[],
        teamGoals: [] as TeamGoal[],
        commonGoals: [] as CommonGoal[],
      }
    }
    const [members, sessions, tags, teamGoals, commonGoals] = await Promise.all([
      // Everyone on the team, including deactivated people: a coach may have met
      // someone who has since rolled off, and their log shouldn't vanish with
      // them.
      sb().from('team_members').select('*').eq('team_id', teamId).order('email'),
      sb().from('coaching_sessions').select('*').eq('team_id', teamId).order('met_on', { ascending: false }),
      sb().from('coaching_session_goals').select('*').eq('team_id', teamId),
      sb().from('team_goals').select('*').eq('team_id', teamId).order('display_order'),
      sb().from('common_goals').select('*').eq('active', true).order('display_order'),
    ])
    if (members.error) throw members.error
    if (sessions.error) throw sessions.error
    if (tags.error) throw tags.error
    if (teamGoals.error) throw teamGoals.error
    if (commonGoals.error) throw commonGoals.error
    return {
      members: (members.data ?? []) as TeamMember[],
      sessions: (sessions.data ?? []) as CoachingSession[],
      tags: (tags.data ?? []) as CoachingSessionGoal[],
      teamGoals: (teamGoals.data ?? []) as TeamGoal[],
      commonGoals: (commonGoals.data ?? []) as CommonGoal[],
    }
  }, [teamId])

  const members = data?.members ?? []

  // Per-person roll-up for the roster: how many 1:1s, and how long since the
  // last one. The gap is the number a coach actually scans this list for.
  const stats = React.useMemo(() => {
    const m = new Map<string, { count: number; last: string | null }>()
    for (const s of data?.sessions ?? []) {
      const prev = m.get(s.member_id) ?? { count: 0, last: null }
      m.set(s.member_id, {
        count: prev.count + 1,
        // Sessions arrive newest-first, so the first one seen for a person wins.
        last: prev.last ?? s.met_on,
      })
    }
    return m
  }, [data?.sessions])

  // Land on someone rather than on an empty pane, but never fight a click: only
  // fill in when nothing is chosen, or when the chosen person is gone.
  React.useEffect(() => {
    if (members.length === 0) return
    if (selected && members.some((m) => m.id === selected)) return
    setSelected(members[0].id)
  }, [members, selected])

  const person = members.find((m) => m.id === selected) ?? null
  const log = (data?.sessions ?? []).filter((s) => s.member_id === selected)

  const teamGoalById = React.useMemo(
    () => new Map((data?.teamGoals ?? []).map((g) => [g.id, g])),
    [data?.teamGoals],
  )
  const commonGoalById = React.useMemo(
    () => new Map((data?.commonGoals ?? []).map((g) => [g.id, g])),
    [data?.commonGoals],
  )
  const tagsBySession = React.useMemo(() => {
    const m = new Map<string, CoachingSessionGoal[]>()
    for (const t of data?.tags ?? []) m.set(t.session_id, [...(m.get(t.session_id) ?? []), t])
    return m
  }, [data?.tags])

  async function remove(s: CoachingSession) {
    if (!window.confirm(`Delete the ${formatDate(s.met_on)} 1:1? This cannot be undone.`)) return
    const { error: err } = await sb().from('coaching_sessions').delete().eq('id', s.id)
    if (err) return window.alert(err.message)
    void refetch()
  }

  return (
    <Page
      title="Coaching"
      description="Your 1:1 log. Visible to DPL coaches only — not to the team lead, and not to the person it's about."
      wide
      actions={
        person && (
          <Button onClick={() => setEditing('new')}>
            <Plus /> Log a 1:1
          </Button>
        )
      }
    >
      {error && <ErrorNote error={error} />}
      {loading && !data ? (
        <Loading />
      ) : members.length === 0 ? (
        <EmptyState
          icon={UserRound}
          title="Nobody on this team yet"
          description="Invite people in Admin, then their coaching log will appear here."
        />
      ) : (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
          {/* ---- roster ---------------------------------------------------- */}
          <div className="space-y-1.5">
            {members.map((m) => {
              const st = stats.get(m.id)
              const active = m.id === selected
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setSelected(m.id)}
                  className={cn(
                    'w-full rounded-md border px-3 py-2.5 text-left transition-colors',
                    active
                      ? 'border-primary/40 bg-primary/10'
                      : 'bg-background hover:bg-accent',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className={cn('min-w-0 truncate text-sm font-medium', !m.active && 'text-muted-foreground')}>
                      {memberName(m)}
                    </span>
                    {m.role !== 'member' && (
                      <Badge variant="muted" className="shrink-0 text-[10px]">
                        {m.role === 'team_lead' ? 'Lead' : 'DPL'}
                      </Badge>
                    )}
                    {!m.active && (
                      <Badge variant="outline" className="shrink-0 text-[10px]">
                        inactive
                      </Badge>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-muted-foreground">{m.email}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {/* "Never" is the entry a coach is looking for, so it says so
                        plainly rather than rendering an em dash. */}
                    {st ? (
                      <>
                        {st.count} {st.count === 1 ? '1:1' : '1:1s'} · last {sinceLabel(st.last as string)}
                      </>
                    ) : (
                      <span className="font-medium text-warning">never met</span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          {/* ---- the selected person's log --------------------------------- */}
          <div className="min-w-0">
            {person && (
              <>
                <div className="mb-3 flex flex-wrap items-baseline gap-x-2">
                  <h2 className="text-lg font-semibold">{memberName(person)}</h2>
                  <span className="text-sm text-muted-foreground">{person.email}</span>
                </div>

                {log.length === 0 ? (
                  <EmptyState
                    icon={HeartHandshake}
                    title={`No 1:1s logged with ${memberName(person)}`}
                    description="Record what you talked about and which goal it was in service of."
                    action={
                      <Button onClick={() => setEditing('new')}>
                        <Plus /> Log the first one
                      </Button>
                    }
                  />
                ) : (
                  <div className="space-y-2.5">
                    {log.map((s) => (
                      <Card key={s.id}>
                        <CardContent className="p-4">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                              <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground">
                                <CalendarDays className="h-3.5 w-3.5" /> {formatDate(s.met_on)}
                              </span>
                              {s.duration_minutes && (
                                <span className="inline-flex items-center gap-1">
                                  <Clock className="h-3 w-3" /> {s.duration_minutes} min
                                </span>
                              )}
                            </div>
                            <div className="flex shrink-0 items-center gap-1.5">
                              <Button variant="ghost" size="icon-sm" onClick={() => setEditing(s)} aria-label="Edit 1:1">
                                <Pencil />
                              </Button>
                              <Button variant="ghost" size="icon-sm" onClick={() => void remove(s)} aria-label="Delete 1:1">
                                <Trash2 />
                              </Button>
                            </div>
                          </div>

                          <GoalChips
                            tags={tagsBySession.get(s.id) ?? []}
                            teamGoalById={teamGoalById}
                            commonGoalById={commonGoalById}
                          />

                          {/* Read-only by default, like the schedule's note: the
                              log is read far more than it is written. */}
                          {s.notes ? (
                            <div className="mt-2.5 rounded-md border bg-muted/30 px-3 py-2">
                              <Markdown source={s.notes} className="text-muted-foreground" />
                            </div>
                          ) : (
                            <p className="mt-2.5 text-sm italic text-muted-foreground">No notes.</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {editing && person && (
        <SessionEditor
          member={person}
          session={editing === 'new' ? undefined : editing}
          teamGoals={data?.teamGoals ?? []}
          commonGoals={data?.commonGoals ?? []}
          existingTags={editing === 'new' ? [] : (tagsBySession.get(editing.id) ?? [])}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void refetch()
          }}
        />
      )}
    </Page>
  )
}

/**
 * What the conversation was in service of. Company goals carry the team's own
 * icon vocabulary; catalog goals carry theirs, with the full sentence in a
 * tooltip because a two-word short label doesn't say what the team is aiming at.
 */
function GoalChips({
  tags,
  teamGoalById,
  commonGoalById,
}: {
  tags: CoachingSessionGoal[]
  teamGoalById: Map<string, TeamGoal>
  commonGoalById: Map<string, CommonGoal>
}) {
  if (tags.length === 0) return null
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1">
      {tags.map((t) => {
        const team = t.team_goal_id ? teamGoalById.get(t.team_goal_id) : undefined
        const common = t.common_goal_id ? commonGoalById.get(t.common_goal_id) : undefined
        const goal = team ?? common
        // The row survives its goal only in the window between another coach's
        // delete and this page's refetch — render nothing rather than a blank chip.
        if (!goal) return null
        const Icon = goalIcon(goal.icon)
        const label = team ? team.title : common?.short_label || common?.title
        return (
          <Tooltip key={t.id}>
            <TooltipTrigger asChild>
              <Badge
                variant={team ? 'secondary' : 'outline'}
                className="max-w-[22rem] cursor-default gap-1 truncate text-[10px] font-normal"
              >
                <Icon className="h-3 w-3 shrink-0" />
                <span className="truncate">{label}</span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-[280px] text-xs">
                {team ? `Company goal — ${team.title}` : `Common goal — ${common?.title}`}
              </p>
            </TooltipContent>
          </Tooltip>
        )
      })}
    </div>
  )
}

/** `toggle` keys, so both goal kinds live in one Set without colliding. */
const teamKey = (id: string) => `team:${id}`
const commonKey = (id: string) => `common:${id}`

function SessionEditor({
  member,
  session,
  teamGoals,
  commonGoals,
  existingTags,
  onClose,
  onSaved,
}: {
  member: TeamMember
  session?: CoachingSession
  teamGoals: TeamGoal[]
  commonGoals: CommonGoal[]
  existingTags: CoachingSessionGoal[]
  onClose: () => void
  onSaved: () => void
}) {
  const [metOn, setMetOn] = React.useState(session?.met_on ?? new Date().toISOString().slice(0, 10))
  const [duration, setDuration] = React.useState(session?.duration_minutes?.toString() ?? '')
  const [notes, setNotes] = React.useState(session?.notes ?? '')
  const [picked, setPicked] = React.useState<Set<string>>(
    () =>
      new Set(
        existingTags.map((t) => (t.team_goal_id ? teamKey(t.team_goal_id) : commonKey(t.common_goal_id as string))),
      ),
  )
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // A 1:1 note is the least recoverable thing anyone types in this portal —
  // nobody else can read it, so nobody else can reconstruct it.
  const dirty = useDirty({ metOn, duration, notes, picked: [...picked] })
  const guard = useDiscardGuard(dirty, onClose)

  function toggle(key: string) {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!metOn) return setError('When did the 1:1 happen?')
    const minutes = duration.trim() ? Number(duration) : null
    if (minutes !== null && (!Number.isFinite(minutes) || minutes <= 0)) {
      return setError('Duration has to be a positive number of minutes.')
    }

    setBusy(true)
    setError(null)
    try {
      // team_id is deliberately absent: the trigger derives it from member_id,
      // which is what stops a session being filed under the wrong team.
      const row = {
        member_id: member.id,
        met_on: metOn,
        duration_minutes: minutes,
        notes: notes.trim() || null,
      }
      const saved = session
        ? await sb().from('coaching_sessions').update(row).eq('id', session.id).select().single()
        : await sb().from('coaching_sessions').insert(row).select().single()
      if (saved.error) throw saved.error
      const sessionId = (saved.data as CoachingSession).id

      // Reconcile the tags rather than deleting and re-inserting the lot, so
      // re-saving an unchanged 1:1 doesn't churn created_at on every chip.
      const before = new Set(
        existingTags.map((t) => (t.team_goal_id ? teamKey(t.team_goal_id) : commonKey(t.common_goal_id as string))),
      )
      const added = [...picked].filter((k) => !before.has(k))
      const dropped = existingTags.filter(
        (t) => !picked.has(t.team_goal_id ? teamKey(t.team_goal_id) : commonKey(t.common_goal_id as string)),
      )

      if (added.length > 0) {
        const { error: err } = await sb()
          .from('coaching_session_goals')
          .insert(
            added.map((k) => {
              const [kind, id] = k.split(':')
              return kind === 'team'
                ? { session_id: sessionId, team_goal_id: id }
                : { session_id: sessionId, common_goal_id: id }
            }),
          )
        if (err) throw err
      }
      if (dropped.length > 0) {
        const { error: err } = await sb()
          .from('coaching_session_goals')
          .delete()
          .in('id', dropped.map((t) => t.id))
        if (err) throw err
      }

      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const chip = (key: string, label: string, icon: string | null, title: string) => {
    const on = picked.has(key)
    const Icon = goalIcon(icon)
    return (
      <button
        key={key}
        type="button"
        onClick={() => toggle(key)}
        aria-pressed={on}
        title={title}
        className={cn(
          'inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors',
          on
            ? 'border-primary/40 bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        )}
      >
        <Icon className="h-3 w-3 shrink-0" />
        <span className="truncate">{label}</span>
      </button>
    )
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DiscardPrompt guard={guard} what="this 1:1 note" />
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle>
              {session ? 'Edit 1:1' : 'Log a 1:1'} with {memberName(member)}
            </DialogTitle>
            <DialogDescription>
              Coach-only. The team lead and {memberName(member)} cannot read this.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="cmet">Met on</Label>
                <Input id="cmet" type="date" value={metOn} onChange={(e) => setMetOn(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cdur">Minutes</Label>
                <Input
                  id="cdur"
                  type="number"
                  min={1}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="30"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cnotes">Notes</Label>
              <Textarea
                id="cnotes"
                rows={7}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="font-mono text-xs"
                placeholder={'What they raised, what you agreed, what to follow up on. Markdown.'}
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Target className="h-3.5 w-3.5" /> In service of
              </Label>
              <p className="text-xs text-muted-foreground">
                Optional, and more than one is fine. A 1:1 often serves both a company goal and one
                of DPL&apos;s.
              </p>

              {teamGoals.length > 0 && (
                <div className="pt-1">
                  <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                    {/* The partner's own words, off their dashboard. */}
                    Company goals
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {teamGoals.map((g) => chip(teamKey(g.id), g.title, g.icon, g.title))}
                  </div>
                </div>
              )}

              <div className="pt-1">
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">Common goals</p>
                <div className="flex flex-wrap gap-1.5">
                  {commonGoals.map((g) =>
                    chip(commonKey(g.id), g.short_label || g.title, g.icon, g.title),
                  )}
                </div>
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={guard.requestClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {session ? 'Save changes' : 'Save 1:1'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
